import { useResponsive } from '@/hooks/use-responsive';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/navigation-menu';
import { AppSidebar } from '../app-sidebar';
import { useSuspenseQuery } from '@tanstack/react-query';
import { primaryNavigationQueryOptions } from '@/services/queryOptions/primaryNavigationQueryOptions';
import { CustomNavLink } from '../custom-navlink';
import { DynamicHeroIcon } from '../dynamic-heroicon';
import { CustomNavLinkExternal } from '../custom-navlink-external';
import { Link } from '@tanstack/react-router';
import ShoppingCartSheet from '../sheets/shopping-cart-sheet';
import './index.css';
import { UserProfile } from '../user-profile';
import { LoginSheet } from '../sheets/login-sheets';
import { AgentProfile } from '../agent-profile';
import { gtmHeaderLogoClick } from '@/utils/events';
import { onClickNavExternal, onClickNavInternal } from '@/utils/navigation';
import { PRIMARY_NAV_TYPE } from '@/constants/app.constants';
import BrandIconBlack from '../icons/brand-icon-black';

export const Navbar = () => {
  const { isDesktop } = useResponsive();
  const { data, isError } = useSuspenseQuery(primaryNavigationQueryOptions);
  const hostname = window.location.origin;

  if (isError) {
    return null;
  }

  return (
    <div className="py-3 md:py-0 bg-white text-black">
      <div className="flex container md:h-20 items-center justify-between lg:justify-center">
        <div className="lg:flex lg:justify-start lg:w-[85%]">
          <Link
            onClick={() => {
              gtmHeaderLogoClick({ clickUrl: `${hostname}/` });
            }}
            to="/"
          >
            <BrandIconBlack className="h-[20px] w-[94px] md:h-[24px] md:w-[112.26x]" />
          </Link>
        </div>

        {isDesktop && (
          <NavigationMenu>
            <NavigationMenuList>
              {data.map((navigationItem) => {
                return (
                  <NavigationMenuItem key={navigationItem.page.id}>
                    {navigationItem.children.length > 0 ? (
                      <NavigationMenuTrigger
                        className="NavigationMenuItem"
                        showIndicator
                      >
                        {navigationItem.page.title}
                      </NavigationMenuTrigger>
                    ) : (
                      <CustomNavLink
                        onClick={() =>
                          onClickNavInternal({
                            url: navigationItem.page.url,
                            title: navigationItem.page.title,
                            navType: PRIMARY_NAV_TYPE,
                          })
                        }
                        to={navigationItem.page.url}
                        search
                        className="NavigationMenuItem group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-0 text-sm font-nunito500 focus:outline-hidden disabled:pointer-events-none disabled:opacity-50"
                      >
                        <div className="relative flex gap-[3px]">
                          {navigationItem.page.title}
                          {navigationItem.page.icon && (
                            <DynamicHeroIcon
                              iconName={navigationItem.page.icon}
                              shape={
                                navigationItem.page.icon_style?.key ?? 'outline'
                              }
                              className="size-[15px] relative top-0.5"
                            />
                          )}
                        </div>
                      </CustomNavLink>
                    )}
                    <NavigationMenuContent className="w-full sm:w-auto flex gap-4 px-[10px] py-[10px]">
                      {/* Calculate number of columns needed based on children length */}
                      {Array.from({
                        length: Math.ceil(navigationItem.children.length / 4),
                      }).map((_, colIndex) => (
                        <div key={colIndex} className="flex flex-col gap-4">
                          {/* Get children for each column */}
                          {navigationItem.children
                            .slice(colIndex * 4, (colIndex + 1) * 4)
                            .map((child) =>
                              child.page.new_window ? (
                                <CustomNavLinkExternal
                                  onClick={() =>
                                    onClickNavExternal({
                                      url: child.page.url,
                                      title: child.page.title,
                                      navType: PRIMARY_NAV_TYPE,
                                    })
                                  }
                                  href={child.page.url}
                                  key={child.page.id}
                                  className="text-sm font-nunito700"
                                >
                                  <div className="flex w-[282px] py-[10px] px-5 justify-between items-center rounded-lg hover:bg-warp-green-400 cursor-pointer text-black">
                                    {child.page.title}
                                    {child.page.icon && (
                                      <DynamicHeroIcon
                                        iconName={child.page.icon}
                                        shape={
                                          child.page?.icon_style?.key ??
                                          'outline'
                                        }
                                        className="size-5"
                                      />
                                    )}
                                  </div>
                                </CustomNavLinkExternal>
                              ) : (
                                <CustomNavLink
                                  onClick={() =>
                                    onClickNavInternal({
                                      url: child.page.url,
                                      title: child.page.title,
                                      navType: PRIMARY_NAV_TYPE,
                                    })
                                  }
                                  to={child.page.url}
                                  className="text-sm font-nunito700"
                                  search
                                  key={child.page.id}
                                >
                                  <div className="flex w-[282px] py-[10px] px-5 justify-between items-center rounded-lg hover:bg-warp-green-400 cursor-pointer text-black">
                                    {child.page.title}
                                    {child.page.icon && (
                                      <DynamicHeroIcon
                                        iconName={child.page.icon}
                                        shape={
                                          child.page?.icon_style?.key ??
                                          'outline'
                                        }
                                        className="size-5"
                                      />
                                    )}
                                  </div>
                                </CustomNavLink>
                              )
                            )}
                        </div>
                      ))}
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                );
              })}
              <NavigationMenuIndicator />
            </NavigationMenuList>
          </NavigationMenu>
        )}
        <div className="flex items-center gap-4 lg:gap-8 lg:justify-end lg:w-full">
          {isDesktop ? (
            <>
              <div className="inline-flex items-center gap-[15px] text-black">
                <AgentProfile />
                <UserProfile />
                <LoginSheet className="flex text-black" />
              </div>
              <div className="inline-flex items-center gap-[3px] text-black">
                <span className="text-xs font-bold font-nunito700">Cart</span>
                <ShoppingCartSheet />
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <ShoppingCartSheet />
                </div>
                <AppSidebar navigationData={data} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
